import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, Package, FileSignature, FilePlus2, Zap, Users as UsersIcon, BookOpenText, UserCircle2, LifeBuoy, KeyRound, Contact, ShieldAlert, FileWarning, FileText, Sparkles, HardHat, Wrench, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth, isAdmin, isPartner, isInstaller, isScintillaStaff } from '@/contexts/AuthContext';
import { useTicketsUnread } from '@/hooks/useTicketsUnread';

export const buildItems = (user) => {
  // === INSTALLATORE: sidebar dedicata, minima ===
  if (isInstaller(user)) {
    return [
      { to: '/installer/interventi', label: 'I miei interventi', icon: Wrench, testid: 'nav-installer-interventi' },
      { to: '/installer/compensi', label: 'I miei compensi', icon: Wallet, testid: 'nav-installer-compensi' },
      { to: '/tickets', label: 'Ticket & Assistenza', icon: LifeBuoy, testid: 'nav-tickets' },
      { to: '/profile', label: 'Il mio profilo', icon: UserCircle2, testid: 'nav-profile' },
    ];
  }
  const items = [{ to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true, testid: 'nav-dashboard' }];
  if (isAdmin(user)) {
    items.push({ to: '/partners', label: 'Aziende Partner', icon: Building2, testid: 'nav-partners' });
  }
  if (isPartner(user) && user?.company_id) {
    items.push({ to: `/partners/${user.company_id}`, label: 'La mia azienda', icon: Building2, testid: 'nav-my-company' });
  }
  if (isAdmin(user) || isPartner(user)) {
    items.push({ to: '/products', label: 'Catalogo Prodotti', icon: Package, testid: 'nav-products' });
    items.push({ to: '/users', label: 'Utenti', icon: UsersIcon, testid: 'nav-users' });
  }
  items.push({ to: '/listino', label: 'Listino & Preventivi', icon: BookOpenText, testid: 'nav-listino' });
  items.push({ to: '/quotes', label: 'I miei Preventivi', icon: FileText, testid: 'nav-quotes' });
  items.push({ to: '/contracts', label: 'Contratti', icon: FileSignature, testid: 'nav-contracts' });
  items.push({ to: '/contracts/new?mode=quote', label: 'Nuovo Preventivo', icon: Sparkles, testid: 'nav-new-quote' });
  items.push({ to: '/contracts/new', label: 'Nuovo Contratto', icon: FilePlus2, testid: 'nav-new-contract', highlight: true });
  items.push({ to: '/customers', label: 'Anagrafiche', icon: Contact, testid: 'nav-customers' });
  // Gestione installazioni: staff Scintilla vede tutto, partner solo i propri
  if (isScintillaStaff(user)) {
    items.push({ to: '/installations', label: 'Gestione Interventi', icon: HardHat, testid: 'nav-installations' });
    items.push({ to: '/installers', label: 'Installatori', icon: Wrench, testid: 'nav-installers' });
    items.push({ to: '/admin/compensation-rates', label: 'Tariffario Compensi', icon: Wallet, testid: 'nav-comp-rates' });
    items.push({ to: '/admin/onboarding', label: 'Onboarding Partner', icon: Sparkles, testid: 'nav-onboarding' });
  }
  if (isAdmin(user) || isPartner(user)) {
    items.push({ to: '/otp-logs', label: 'Log Firme OTP', icon: KeyRound, testid: 'nav-otp-logs' });
  }
  if (isAdmin(user)) {
    items.push({ to: '/security/login-attempts', label: 'Tentativi di Accesso', icon: ShieldAlert, testid: 'nav-login-attempts' });
    items.push({ to: '/admin/pdf-errors', label: 'Log Errori PDF', icon: FileWarning, testid: 'nav-pdf-errors' });
  }
  items.push({ to: '/tickets', label: 'Ticket & Assistenza', icon: LifeBuoy, testid: 'nav-tickets' });
  items.push({ to: '/profile', label: 'Il mio profilo', icon: UserCircle2, testid: 'nav-profile' });
  return items;
};

export const Sidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const items = buildItems(user);
  const ticketsUnread = useTicketsUnread(!!user, location.pathname);

  // White-label branding: admin -> Scintilla Hub; partner/agent -> nome azienda partner
  const isAdminUser = isAdmin(user);
  const logoBg = isAdminUser ? '#0A0E0B' : (user?.company?.primary_color || '#0A0E0B');
  const logoFg = isAdminUser ? '#9FFF5C' : (user?.company?.accent_color || '#9FFF5C');

  return (
    <aside className="hidden lg:flex w-[260px] shrink-0 flex-col border-r bg-card">
      <div className="flex items-center gap-2 px-5 h-14 border-b">
        <div className="size-9 rounded-xl grid place-items-center overflow-hidden" style={{ background: logoBg }}>
          {!isAdminUser && user?.company?.logo_data_url ? (
            <img src={user.company.logo_data_url} alt="" className="max-w-full max-h-full object-contain" />
          ) : (
            <Zap className="size-4" style={{ color: logoFg }} />
          )}
        </div>
        <div className="min-w-0">
          <div className="font-heading text-base font-semibold leading-none truncate">CRM Utility Boost</div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {items.map(item => {
          const Icon = item.icon;
          // Match considerando anche il query string per distinguere le voci
          // "Nuovo Preventivo" (?mode=quote) da "Nuovo Contratto" (path uguale, no query)
          const [itemPath, itemQuery] = item.to.split('?');
          let active;
          if (item.exact) {
            active = location.pathname === itemPath && (itemQuery ? location.search.includes(itemQuery) : true);
          } else if (itemQuery) {
            active = location.pathname.startsWith(itemPath) && location.search.includes(itemQuery);
          } else if (itemPath === '/contracts/new') {
            // "Nuovo Contratto" attivo solo se NON è in modalità preventivo
            active = location.pathname.startsWith(itemPath) && !location.search.includes('mode=quote');
          } else {
            active = location.pathname.startsWith(itemPath);
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={cn(
                'flex items-center gap-3 px-3 h-10 rounded-lg text-sm transition-colors',
                active
                  ? 'bg-secondary text-foreground border border-border'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60',
                item.highlight && !active && 'text-foreground bg-accent/10 border border-accent/30',
              )}
            >
              <Icon className="size-4" />
              <span className="truncate flex-1">{item.label}</span>
              {item.testid === 'nav-tickets' && ticketsUnread > 0 && (
                <span
                  className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold grid place-items-center tabular-nums"
                  data-testid="tickets-unread-badge"
                >
                  {ticketsUnread > 99 ? '99+' : ticketsUnread}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 text-[11px] text-muted-foreground border-t">
        <div className="font-medium text-foreground">CRM Utility Boost</div>
        <div>{isAdminUser ? 'Smart Utility Agency · Scintilla Hub' : (user?.company?.name || 'Scintilla Hub')}</div>
      </div>
    </aside>
  );
};
